/**
 * Profile page – orchestrator
 * Imports and composes modules from profile/ directory
 */

import { render } from '../main';
import { store } from '../state';
import { getCurrentPath } from '../router';
import { getSavedTheme } from '../theme';
import { loadPhotos, setupAutoSave } from './profile/index';
import {
  disablePasswordManagers,
  setupHeightSync,
  setupBodyPnta,
  updateAgeDisplay,
  updateLocationDisplay,
} from './profile/display-updaters';
import { setupChildrenHandlers } from './profile/children-manager';
import { setupConditionalFields, setupDropdownWithOther } from './profile/conditional-fields';
import { renderAboutTab } from './profile/sections/about-tab';
import { renderOwnProfileTab } from './profile-view';
import {
  renderSettingsTab,
  renderDeleteModal,
  setupLogout,
  setupDeleteAccount,
  setupHandle,
  setupThemeToggle,
  setupNotificationPrefs,
  setupInviteFriends,
} from './profile/sections/settings-tab';

const TAB_ROUTES: Record<string, string> = {
  '/about-me': 'about',
  '/my-photos': 'photos',
  '/view-profile': 'view',
  '/settings': 'settings',
};

const TAB_TO_ROUTE: Record<string, string> = {
  about: '/about-me',
  photos: '/my-photos',
  view: '/view-profile',
  settings: '/settings',
};

// Track whether the view tab has been loaded (lazy-load once)
let viewTabLoaded = false;

export async function renderProfile(): Promise<void> {
  viewTabLoaded = false;
  const state = store.getState();
  const profile = state.user?.profile;
  const preferences = state.user?.preferences;

  // Extract profile_json data
  const pj = profile?.profile_json || {};
  const identity = pj.identity || {};
  const body = pj.body || {};
  const lifestyle = pj.lifestyle || {};
  const pets = pj.pets || {};
  const kids = pj.kids || {};
  const background = pj.background || {};
  const about = pj.about || {};
  const relationship = pj.relationship || {};
  const profilePrefs = pj.preferences || {};
  const sexual = pj.sexual || {};
  const disclosures = pj.disclosures || {};

  // Extract filters_json and visibility data
  const prefs = preferences?.filters_json || {};
  const visi = preferences?.visi_json || {};
  const units = prefs.app?.units || 'imperial';
  const theme = getSavedTheme();

  // Determine active tab from route
  const activeTab = TAB_ROUTES[getCurrentPath()] || 'about';

  render(`
    <div class="view-frame profile-edit-container">
      <!-- Tabs -->
      <div class="seg-bar"><div class="seg seg-full">
        <button class="seg-btn${activeTab === 'about' ? ' active' : ''}" data-tab="about">About Me</button>
        <button class="seg-btn${activeTab === 'view' ? ' active' : ''}" data-tab="view">View Profile</button>
        <button class="seg-btn${activeTab === 'settings' ? ' active' : ''}" data-tab="settings">Settings</button>
      </div></div>

      ${renderAboutTab({
        userId: state.user!.id,
        profile: profile || {},
        identity,
        body,
        lifestyle,
        pets,
        kids,
        background,
        about,
        relationship,
        profilePrefs,
        sexual,
        disclosures,
        genders: state.genders,
        relationshipStatuses: state.relationshipStatuses,
        units,
        visiJson: visi,
        isActive: activeTab === 'about',
      })}

      <!-- View Profile Tab -->
      <div class="view-scroll seg-panel${activeTab === 'view' ? ' active' : ''}" id="tab-view">
        <div class="pub-loading"><div class="spinner"></div></div>
      </div>

      <!-- My Photos Tab -->
      <div class="view-scroll seg-panel${activeTab === 'photos' ? ' active' : ''}" id="tab-photos">
        <div class="photos-section photos-section-vertical">
          <div id="photo-grid" class="photo-grid photo-grid-vertical">
            <div class="photo-grid-loading">
              <div class="spinner"></div>
            </div>
          </div>
        </div>
      </div>

      ${renderSettingsTab({
        email: state.user?.email || '',
        emailVerified: !!state.user?.email_verified,
        phoneNumber: state.user?.phone_number || '',
        handle: profile?.handle || '',
        discoverable: preferences?.discoverable !== false,
        liveTypingEnabled: preferences?.live_typing_enabled !== false,
        showMatureContent: preferences?.show_mature_content ?? false,
        showExplicitContent: preferences?.show_explicit_content ?? false,
        units,
        theme,
        isActive: activeTab === 'settings',
        isAdmin: !!state.user?.email?.endsWith('@914-6.com'),
      })}
    </div>

    ${renderDeleteModal()}
  `);

  // Setup all functionality
  disablePasswordManagers();
  setupAutoSave(updateLocationDisplay, updateAgeDisplay);
  setupLogout();
  setupDeleteAccount();
  setupHeightSync();
  setupBodyPnta();
  setupTabs();

  // Auto-load view tab if landing on /view-profile directly
  if (activeTab === 'view') {
    viewTabLoaded = true;
    const viewContainer = document.getElementById('tab-view');
    if (viewContainer) renderOwnProfileTab(viewContainer);
  }

  setupSectionToggles();
  setupConditionalFields();
  setupDropdownWithOther();
  setupHandle();
  setupThemeToggle();
  setupNotificationPrefs();
  setupInviteFriends();
  setupChildrenHandlers();
  updateAgeDisplay();
  updateLocationDisplay();
  loadPhotos();
}

function setupTabs(): void {
  const tabs = document.querySelectorAll('.seg-btn');
  const contents = document.querySelectorAll('.seg-panel');

  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      const el = tab as HTMLElement;
      const tabName = el.dataset.tab;
      const targetId = `tab-${tabName}`;

      // Update active tab
      tabs.forEach(t => t.classList.remove('active'));
      tab.classList.add('active');

      // Update active content
      contents.forEach(c => c.classList.remove('active'));
      document.getElementById(targetId)?.classList.add('active');

      // Lazy-load view tab content on first click
      if (tabName === 'view' && !viewTabLoaded) {
        viewTabLoaded = true;
        const viewContainer = document.getElementById('tab-view');
        if (viewContainer) renderOwnProfileTab(viewContainer);
      }

      // Update URL without triggering hashchange (no re-render)
      const route = TAB_TO_ROUTE[tabName!];
      if (route) {
        history.replaceState(null, '', '#' + route);
        store.setCurrentView(route);
      }
    });
  });
}

function setupSectionToggles(): void {
  const headers = document.querySelectorAll('.form-section-header');

  headers.forEach(header => {
    header.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('.visi-check')) return;
      e.preventDefault();
      e.stopPropagation();
      const sectionName = (header as HTMLElement).dataset.section;
      const content = document.getElementById(`section-${sectionName}`);

      if (content) {
        header.classList.toggle('collapsed');
        content.classList.toggle('collapsed');
      }
    });
  });
}
